"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import Logo from "../../../../public/assets/melco-logo.webp";  
import { usePathname } from "next/navigation";
import { MenuItems } from "../MenuItem";

// Custom SVG Icons
const PhoneIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
    <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

const MobileIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
    <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
  </svg>
);

const ChevronDownIcon = ({ className = "" }: { className?: string }) => (
  <svg className={`w-4 h-4 fill-current ${className}`} viewBox="0 0 24 24">
    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
  </svg>
);

const DoubleArrowUpIcon = () => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
    <path d="M6 17.59L7.41 19 12 14.42 16.59 19 18 17.59l-6-6zM6 11l1.41 1.41L12 7.83l4.59 4.58L18 11l-6-6z" />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const TwitterIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const YoutubeIcon = () => (
  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const LinkedinIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const DesktopHeader = () => {
  const pathname = usePathname();
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScroll, setShowScroll] = useState(false);

  const handleMouseEnter = (id: number) => setOpenDropdownId(id);
  const handleMouseLeave = () => setOpenDropdownId(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScroll(window.scrollY > 200);
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderLink = (href: string, children: React.ReactNode, className = "") => {
    const isExternal = href.startsWith("http");
    if (isExternal) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {children}
        </a>
      );
    }
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? "shadow-lg" : ""
        }`}
      >
        {/* TOP BAR - YELLOW DESIGN WITH ANGLED CUT */}
        <div
          className={`transition-colors duration-300 overflow-hidden relative ${
            isScrolled ? "bg-white" : "bg-[#343a40]"
          }`}
        >
          <div className="w-full flex justify-end items-center h-8">
            <div
              className="bg-[#ffb400] w-[70%] justify-between text-white text-xs font-semibold flex items-center h-full relative"
              style={{
                clipPath: "polygon(25px 0%, 100% 0%, 100% 100%, 0% 100%)",
                paddingLeft: "35px",
              }}
            >
              {/* Contact Info Items */}
              <div className="flex items-center gap-6 pr-6 border-r border-black/10">
                <a
                  href="tel:+91-129-2276475"
                  className="flex items-center gap-2 hover:opacity-80 transition"
                >
                  <PhoneIcon />
                  <span>+91-129-2276475</span>
                </a>
                <a
                  href="tel:+91-129-2276466"
                  className="flex items-center gap-2 hover:opacity-80 transition"
                >
                  <MobileIcon />
                  <span>+91-129-2276466</span>
                </a>
                <a
                  href="mailto:info@melcoindia.in"
                  className="flex items-center gap-2 hover:opacity-80 transition"
                >
                  <MailIcon />
                  <span>info@melcoindia.in</span>
                </a>
              </div>

              {/* Social Icons */}
              <div className="flex items-center">
                <a
                  href="#"
                  className="px-3 border-r border-black/10 hover:bg-black/10 h-8 flex items-center transition"
                  aria-label="Facebook"
                >
                  <FacebookIcon />
                </a>
                <a
                  href="#"
                  className="px-3 border-r border-black/10 hover:bg-black/10 h-8 flex items-center transition"
                  aria-label="Twitter"
                >
                  <TwitterIcon />
                </a>
                <a
                  href="#"
                  className="px-3 border-r border-black/10 hover:bg-black/10 h-8 flex items-center transition"
                  aria-label="Youtube"
                >
                  <YoutubeIcon />
                </a>
                <a
                  href="#"
                  className="px-3 border-r border-black/10 hover:bg-black/10 h-8 flex items-center transition"
                  aria-label="LinkedIn"
                >
                  <LinkedinIcon />
                </a>
                <a
                  href="#"
                  className="px-3 hover:bg-black/10 h-8 flex items-center transition"
                  aria-label="Instagram"
                >
                  <InstagramIcon />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN NAVIGATION BAR */}
        <nav
          className={`transition-colors duration-300 ${
            isScrolled ? "bg-[#343a40]" : "bg-white"
          }`}
        >
          <div className="container mx-auto px-4 flex justify-between items-center h-18">
            {/* Logo */}
            <div className="flex items-center">
              <Link href="/" className="flex items-center">
                <Image
                  src={Logo}
                  alt="Construction Logo"
                  width={180}
                  height={50}
                  className="h-12 w-auto object-contain"
                />
              </Link>
            </div>

            {/* Menu Nav Links */}
            <ul className="flex items-center gap-7">
              {MenuItems.map((item) => {
                const isActive = pathname === item.href;
                return (
                  <li
                    key={item.id}
                    className="relative group py-6"
                    onMouseEnter={() => handleMouseEnter(+item.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    {renderLink(
                      item.href ?? "#",
                      <>
                        <span className="flex items-center gap-1.5">
                          {item.name}
                          {item.badge && (
                            <span className="bg-[#ffb400] text-black font-extrabold text-[10px] px-1.5 py-0.5 rounded tracking-wider uppercase">
                              {item.badge}
                            </span>
                          )}
                        </span>
                        {item.submenu && (
                          <ChevronDownIcon className="transition-transform duration-200 group-hover:rotate-180" />
                        )}
                      </>,
                      `flex items-center gap-1 text-sm font-bold tracking-wide transition-colors ${
                        isActive
                          ? "text-[#ffb400]"
                          : isScrolled
                          ? "text-white hover:text-[#ffb400]"
                          : "text-gray-800 hover:text-[#ffb400]"
                      }`
                    )}

                    {/* Dropdown Menu */}
                    {item.submenu && (
                      <ul
                        className={`absolute left-0 top-[100%] w-[220px] bg-[#2b3035] text-white shadow-2xl rounded-b-md flex flex-col z-50 py-2 transition-all duration-300 origin-top transform border-t-2 border-[#ffb400] ${
                          openDropdownId === +item.id
                            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                            : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                        }`}
                      >
                        {item.submenu.map((sub) => (
                          <li
                            key={`submenu-${sub.id}`}
                            className="hover:bg-[#343a40] transition"
                          >
                            {renderLink(
                              sub.href,
                              sub.name,
                              "text-xs px-4 py-2.5 block text-gray-200 hover:text-[#ffb400] transition"
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      </header>

      {/* Scroll To Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 p-3 z-50 rounded-full bg-[#ffb400] text-black shadow-xl transition-all duration-300 ${
          showScroll
            ? "opacity-100 scale-100"
            : "opacity-0 scale-75 pointer-events-none"
        } hover:bg-white hover:text-black`}
        aria-label="Scroll to top"
      >
        <DoubleArrowUpIcon />
      </button>
    </>
  );
};

export default DesktopHeader;